/**
 * ## How to add a new language (or dialect)
 *
 * Step 1: generate translation JSON files across all libs
 * - Run command: `npm run i18n:init-language -- <lang> <base?> [dry-run]`
 *
 * - Example (new language): `npm run i18n:init-language -- pl en`
 * - Example (dialect): `npm run i18n:init-language -- en-au en`
 * - Example (dry-run): `npm run i18n:init-language -- fr-ca fr dry-run`
 *
 * This creates `libs/<lib>/i18n/<lang>.json` everywhere. Content is cloned from
 * `<base>.json` in each lib when that file exists; if `<base>.json` is missing but
 * `en.json` exists, content is cloned from `en.json` (then translate string values
 * to the target locale). If neither exists, the new file is `{}`.
 *
 * Step 2: add mapping here in `resolveCoreLanguage()` in core.i18n.ts
 *
 * - Set `translateLang` to match the JSON filename (e.g. `pl`, `en-au`)
 * - Set `momentLocale` to the moment locale you want
 * - Set `dateFnsLocaleId` to the date-fns locale id used in `core.effects.ts`
 *   (e.g. `pl`, `enAU`, `zhCN`, `enUS`)
 * - Set `pickerLocale` to the BCP 47 tag for Ionic datetime / `localStorage` `locale`
 *
 * Step 3: language is applied automatically in `CoreEffectsPlatform.platformReady` and `CoreEffects.applyUserLanguage`
 *
 * - Browser/default language on app start:
 *   - `CoreEffectsPlatform.platformReady` uses `resolveCoreLanguage(browserLang, cultureLang)`
 * - User language updates at runtime:
 *   - `CoreEffects.applyUserLanguage` uses `resolveCoreLanguage(language)`
 *
 * Step 4: date-fns default locale setup in `saveOnceLoggedUser()` in core.effects.ts
 *
 * - `CoreEffects.saveOnceLoggedUser` calls `resolveCoreLanguage(login.language).dateFnsLocaleId`
 *   and selects the matching date-fns locale for `setDefaultOptions(...)`.
 *
 * Step 5: Ionic datetime / `localStorage` locale
 *
 * - Use `resolvePickerLocale(language, cultureLanguage?)` (or `pickerLocale` on the resolved object)
 *   for BCP 47 tags stored in `localStorage` under `locale` (see onboarding, timeline, form inputs).
 */

export type CoreResolvedLanguage = {
  /**
   * Language code used by ngx-translate loader (for example: `libs/core/i18n/en.json`).
   */
  translateLang: string;
  /**
   * Locale code used by moment.
   */
  momentLocale: string;
  /**
   * Locale id to use for date-fns (resolved in the caller).
   */
  dateFnsLocaleId?: string;
  /**
   * BCP 47 tag for Ionic datetime and `localStorage` key `locale`.
   */
  pickerLocale: string;
};

/** Default when the language is unknown, missing, or not mapped. */
const RESOLVED_ENGLISH: CoreResolvedLanguage = {
  translateLang: 'en',
  momentLocale: 'en',
  dateFnsLocaleId: 'enUS',
  pickerLocale: 'en-US'
};

function normalizeLang(raw?: string | null): string {
  if (raw === null || raw === undefined) {
    return '';
  }
  return String(raw).trim().replace(/_/g, '-').toLowerCase();
}

/**
 * Resolve a user/browser language to a translate language and moment locale.
 *
 * Notes:
 * - The app's translations are stored as JSON files keyed by `translateLang`.
 * - For dialects (en-AU, en-CA, fr-CA), we keep moment's locale specific,
 *   but we can map translations to a base language (en/fr) if desired.
 * - Unknown or empty input falls back to English (`en` / `en-US`), never null.
 */
export function resolveCoreLanguage(
  language: string | null | undefined,
  cultureLanguage?: string | null
): CoreResolvedLanguage {
  const lang = normalizeLang(language);
  const culture = normalizeLang(cultureLanguage);

  // Prefer culture (e.g. "en-au") over generic language (e.g. "en")
  const candidate = culture || lang;

  switch (candidate) {
    // Arabic: historically moment has used "fr" fallback in this codebase
    case 'ar':
      return {
        translateLang: 'ar',
        momentLocale: 'fr',
        dateFnsLocaleId: 'fr',
        pickerLocale: 'ar-AE'
      };

    case 'de':
      return {
        translateLang: 'de',
        momentLocale: 'de',
        dateFnsLocaleId: 'de',
        pickerLocale: 'de-DE'
      };
    case 'en':
      return RESOLVED_ENGLISH;
    case 'es':
      return {
        translateLang: 'es',
        momentLocale: 'es',
        dateFnsLocaleId: 'es',
        pickerLocale: 'es-ES'
      };
    case 'fi':
      return {
        translateLang: 'fi',
        momentLocale: 'fi',
        dateFnsLocaleId: 'fi',
        pickerLocale: 'fi-FI'
      };
    case 'fr':
      return {
        translateLang: 'fr',
        momentLocale: 'fr',
        dateFnsLocaleId: 'fr',
        pickerLocale: 'fr-FR'
      };
    case 'hr':
      return {
        translateLang: 'hr',
        momentLocale: 'hr',
        dateFnsLocaleId: 'hr',
        pickerLocale: 'hr-HR'
      };
    case 'hu':
      return {
        translateLang: 'hu',
        momentLocale: 'hu',
        dateFnsLocaleId: 'hu',
        pickerLocale: 'hu-HU'
      };
    case 'nl':
      return {
        translateLang: 'nl',
        momentLocale: 'nl',
        dateFnsLocaleId: 'nl',
        pickerLocale: 'nl-NL'
      };
    case 'nl-be':
      return {
        translateLang: 'nl-be',
        momentLocale: 'nl-be',
        dateFnsLocaleId: 'nlBE',
        pickerLocale: 'nl-BE'
      };
    case 'it':
      return {
        translateLang: 'it',
        momentLocale: 'it',
        dateFnsLocaleId: 'it',
        pickerLocale: 'it-IT'
      };
    case 'ja':
      return {
        translateLang: 'ja',
        momentLocale: 'ja',
        dateFnsLocaleId: 'ja',
        pickerLocale: 'ja-JP'
      };

    // dialects
    case 'es-us':
      return {
        translateLang: 'es-us',
        momentLocale: 'es',
        dateFnsLocaleId: 'es',
        pickerLocale: 'es-US'
      };

    case 'es-ar':
      // No dedicated moment/date-fns locale data for es-AR; keep base es formatting.
      return {
        translateLang: 'es-ar',
        momentLocale: 'es',
        dateFnsLocaleId: 'es',
        pickerLocale: 'es-AR'
      };

    case 'en-au':
      return {
        translateLang: 'en-au',
        momentLocale: 'en-au',
        dateFnsLocaleId: 'enAU',
        pickerLocale: 'en-AU'
      };
    case 'en-ca':
      return {
        translateLang: 'en-ca',
        momentLocale: 'en-ca',
        dateFnsLocaleId: 'enCA',
        pickerLocale: 'en-CA'
      };
    case 'en-gb':
      return {
        translateLang: 'en-gb',
        momentLocale: 'en-gb',
        dateFnsLocaleId: 'enGB',
        pickerLocale: 'en-GB'
      };
    case 'fr-ca':
      return {
        translateLang: 'fr-ca',
        momentLocale: 'fr-ca',
        dateFnsLocaleId: 'frCA',
        pickerLocale: 'fr-CA'
      };
    case 'en-nz':
      return {
        translateLang: 'en-nz',
        momentLocale: 'en-nz',
        dateFnsLocaleId: 'enNZ',
        pickerLocale: 'en-NZ'
      };
    case 'de-at':
      return {
        translateLang: 'de-at',
        momentLocale: 'de-at',
        dateFnsLocaleId: 'deAT',
        pickerLocale: 'de-AT'
      };
    case 'fr-be':
      // No dedicated moment/date-fns locale data for fr-BE; keep base fr formatting.
      return {
        translateLang: 'fr-be',
        momentLocale: 'fr',
        dateFnsLocaleId: 'fr',
        pickerLocale: 'fr-BE'
      };
    case 'sv-fi':
      // No dedicated moment/date-fns locale data for sv-FI; keep base sv formatting.
      return {
        translateLang: 'sv-fi',
        momentLocale: 'sv',
        dateFnsLocaleId: 'sv',
        pickerLocale: 'sv-FI'
      };

    case 'pl':
      return {
        translateLang: 'pl',
        momentLocale: 'pl',
        dateFnsLocaleId: 'pl',
        pickerLocale: 'pl-PL'
      };

    case 'pt':
      return {
        translateLang: 'pt',
        momentLocale: 'pt',
        dateFnsLocaleId: 'pt',
        pickerLocale: 'pt-PT'
      };

    case 'pt-br':
      return {
        translateLang: 'pt-br',
        momentLocale: 'pt-br',
        dateFnsLocaleId: 'ptBR',
        pickerLocale: 'pt-BR'
      };

    case 'ro':
      return {
        translateLang: 'ro',
        momentLocale: 'ro',
        dateFnsLocaleId: 'ro',
        pickerLocale: 'ro-RO'
      };

    case 'sv':
      return {
        translateLang: 'sv',
        momentLocale: 'sv',
        dateFnsLocaleId: 'sv',
        pickerLocale: 'sv-SE'
      };

    // Chinese handling
    case 'zh':
    case 'zh-cn':
    case 'zh-hans':
      return {
        translateLang: 'zh',
        momentLocale: 'zh',
        dateFnsLocaleId: 'zhCN',
        pickerLocale: 'zh-CN'
      };

    case 'zh-tw':
    case 'zh-hant':
    case 'zh-hk':
    case 'tw':
      return {
        translateLang: 'tw',
        momentLocale: 'zh',
        dateFnsLocaleId: 'zhTW',
        pickerLocale: 'zh-TW'
      };
  }

  // Fallback: if we got "en-xx" or "fr-xx" but don't have a dedicated mapping,
  // keep translations on base language while still allowing moment to format dates
  // using the base language (or the original candidate if you later add files).
  if (candidate.startsWith('en-')) {
    return RESOLVED_ENGLISH;
  }
  if (candidate.startsWith('fr-')) {
    return {
      translateLang: 'fr',
      momentLocale: 'fr',
      dateFnsLocaleId: 'fr',
      pickerLocale: 'fr-FR'
    };
  }
  if (candidate.startsWith('es-')) {
    return {
      translateLang: 'es',
      momentLocale: 'es',
      dateFnsLocaleId: 'es',
      pickerLocale: 'es-ES'
    };
  }

  return RESOLVED_ENGLISH;
}

/**
 * BCP 47 locale for Ionic datetime and `localStorage` key `locale`.
 * Unknown or missing languages fall back to `en-US`.
 * Non-string values from the API are coerced safely (never throws).
 */
export function resolvePickerLocale(
  language: string | null | undefined,
  cultureLanguage?: string | null
): string {
  return resolveCoreLanguage(language, cultureLanguage).pickerLocale;
}
